import { Controller } from '@hotwired/stimulus'

import { CreateMessageService } from 'services/messages/createMessageService'
import { FileAttachmentService } from 'services/messages/fileAttachmentService'

import { loadDeviceKeyFromSessionOnce, decryptSessionData } from '../encryption/encryption'

import { loadEncryptedSessionKey } from '../encryption/keyHandling'
import { injectLinksToTextMessage, sanitizeMessageBody } from '../services/messages'

import ErrorMessageController from './document_upload/error_message_controller'

interface InstitutionPublicKey {
  id: string
  public_exponent: string
  modulus: string
}

export default class extends Controller {
  static values = {
    encryptedBody: String,
    sessionKey: String,
    sessionKeyPatientPublicKeyId: String,
    institutionId: String,
    patientUserId: String,
    institutionKeyPair: Object,
    isTextDecrypted: { type: Boolean, default: false },
    allowedFileTypes: Array<String>,
    maxFileSizeBytes: Number,
  }

  static targets = [
    'messageText',
    'messageContainer',
    'textarea',
    'errorMessage',
    'fileInput',
    'uploadFilesContainer',
    'showFilesUploadContainerButton',
    'hideFilesUploadContainerButton',
    'form',
    'fileAttachmentItemTemplate',
    'fileAttachmentListTemplate',
  ]

  private fileAttachmentService: FileAttachmentService = new FileAttachmentService(this)
  private createMessageService: CreateMessageService = new CreateMessageService(this)

  initialize() {
    if (this.hasMessageContainerTarget) {
      this.messageContainerTarget.hidden = true
    }
  }

  async connect() {
    await loadDeviceKeyFromSessionOnce()

    let messageTextHtml: string | null = ''

    if (this.encryptedBodyValue) {
      try {
        const decryptedSessionKey = await loadEncryptedSessionKey(
          this.sessionKeyValue,
          this.sessionKeyPatientPublicKeyIdValue,
        )
        const decryptedText = await decryptSessionData(this.encryptedBodyValue, decryptedSessionKey)
        const sanitizedText = sanitizeMessageBody(decryptedText)
        messageTextHtml = injectLinksToTextMessage(sanitizedText)
      } catch {
        messageTextHtml = this.messageTextTarget.textContent
      } finally {
        this.isTextDecryptedValue = true
      }

      if (messageTextHtml) {
        this.messageTextTarget.innerHTML = messageTextHtml
      }
    }

    if (this.hasTextareaTarget) {
      this.textareaTarget.value = ''
      this.textareaTarget.focus()
    }
  }

  async create(event: Event) {
    event.stopPropagation()
    event.preventDefault()

    const files = this.fileAttachmentService.files
    this.createMessageService.create(files)
  }

  onFileChanged(e: InputEvent) {
    this.fileAttachmentService.onFileChanged(e)
  }

  showFilesUploadContainer(event: Event) {
    event.stopPropagation()
    event.preventDefault()

    this.fileAttachmentService.showFilesUploadContainer()
  }

  hideFilesUploadContainer(event: Event) {
    event.stopPropagation()
    event.preventDefault()

    this.fileAttachmentService.hideFilesUploadContainer()
  }

  isTextDecryptedValueChanged() {
    if (this.hasMessageContainerTarget && this.isTextDecryptedValue) {
      this.messageContainerTarget.hidden = false
    }

    document
      .querySelector('#scroll-anchor')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  get errorMessage() {
    return this.application.getControllerForElementAndIdentifier(
      this.errorMessageTarget,
      'document-upload--error-message',
    ) as ErrorMessageController
  }

  declare encryptedBodyValue: string
  declare sessionKeyValue: string
  declare sessionKeyPatientPublicKeyIdValue: string
  declare institutionIdValue: string
  declare patientUserIdValue: string
  declare institutionKeyPairValue: InstitutionPublicKey
  declare isTextDecryptedValue: Boolean
  declare fileInputTarget: HTMLInputElement
  declare uploadFilesContainerTarget: HTMLInputElement
  declare showFilesUploadContainerButtonTarget: HTMLInputElement
  declare hideFilesUploadContainerButtonTarget: HTMLInputElement

  declare messageTextTarget: HTMLParagraphElement
  declare messageContainerTarget: HTMLDivElement
  declare textareaTarget: HTMLTextAreaElement
  declare errorMessageTarget: HTMLDivElement
  declare hasTextareaTarget: Boolean
  declare hasMessageTextTarget: Boolean
  declare hasMessageContainerTarget: Boolean
  declare hasFileInputTarget: Boolean
  declare formTarget: HTMLFormElement
  declare fileAttachmentItemTemplateTarget: HTMLTemplateElement
  declare fileAttachmentListTemplateTarget: HTMLTemplateElement
  declare allowedFileTypesValue: string[]
  declare maxFileSizeBytesValue: number
}
